import { useCallback } from 'react';
import { useTransactionDetailSectionContext } from '../context/TransactionDetailSectionContext';
import { CustomField, SimpleField, TooltipItem, Transaction } from '../types';

// Helper function to safely execute compute functions
const safeExecuteCompute = (
  compute: (transaction: Transaction) => string | number,
  transaction: Transaction,
  fallback: string = ''
): string => {
  try {
    const result = compute(transaction);
    return String(result);
  } catch (error) {
    return fallback;
  }
};

// Type guard to check if field has tooltip properties
const hasTooltip = (
  field: SimpleField
): field is SimpleField & {
  tooltip: string;
  tooltipIcon: 'help' | 'info';
} => {
  return 'tooltip' in field && 'tooltipIcon' in field;
};

// Type guard to check if field has contentTooltip properties
const hasContentTooltip = (
  field: SimpleField
): field is SimpleField & {
  contentTooltip: string | ((transaction: Transaction) => string);
  contentTooltipIcon: 'help' | 'info';
} => {
  return 'contentTooltip' in field && 'contentTooltipIcon' in field;
};

export const useFieldRenderer = (
  transaction: Transaction,
  transactionIndex?: number
) => {
  const { isSelected, tooltips } = useTransactionDetailSectionContext();

  const renderField = useCallback(
    (field: string | CustomField) => {
      // Normalize to SimpleField
      const normalizedField: CustomField =
        typeof field === 'string' ? { key: field, label: field } : field;

      const { key, label, compute } = normalizedField;

      // Safely extract optional properties
      const tooltip = hasTooltip(normalizedField)
        ? normalizedField.tooltip
        : undefined;
      const tooltipIcon = hasTooltip(normalizedField)
        ? normalizedField.tooltipIcon
        : undefined;
      const contentTooltipFromField = hasContentTooltip(normalizedField)
        ? normalizedField.contentTooltip
        : undefined;
      const contentTooltipIconFromField = hasContentTooltip(normalizedField)
        ? normalizedField.contentTooltipIcon
        : undefined;

      // Get content
      let content: string;
      if (compute) {
        content = safeExecuteCompute(compute, transaction, '');
      } else {
        content =
          transaction[key] !== undefined ? String(transaction[key]) : '';
      }

      // Tooltips
      const titleTooltip = tooltip;
      const titleTooltipIcon = tooltipIcon;

      // Auto content tooltip from context (if available)
      let tooltipFromInference: TooltipItem | undefined;
      if (transactionIndex !== undefined && tooltips) {
        const tooltipKey = `${transactionIndex}_${key}`;
        const tooltipData = tooltips[tooltipKey];
        if (tooltipData?.content && typeof tooltipData.content === 'string') {
          tooltipFromInference = {
            message: tooltipData.content,
            icon: 'info',
          };
        } else if (
          tooltipData?.content &&
          typeof tooltipData.content === 'object'
        ) {
          tooltipFromInference = tooltipData.content;
        }
      }

      const contentTooltipMessage =
        content && tooltipFromInference
          ? tooltipFromInference.message
          : typeof contentTooltipFromField === 'function'
          ? contentTooltipFromField(transaction)
          : contentTooltipFromField;

      const contentTooltipIcon =
        content && tooltipFromInference
          ? tooltipFromInference.icon
          : contentTooltipIconFromField;

      const componentType = normalizedField.componentType;

      return {
        key,
        title: label,
        content,
        titleTooltip,
        titleTooltipIcon,
        contentTooltip: contentTooltipMessage,
        contentTooltipIcon: contentTooltipIcon,
        isSelected,
        componentType,
      };
    },
    [transaction, isSelected, tooltips, transactionIndex]
  );

  return { renderField };
};
